import { useCallback, useEffect, useState } from 'react';
import { PENDING_CHECKS } from '@/lib/emailDomainHealth';
import {
  getEmailDomainHealthCacheState,
  requestEmailDomainHealth,
  subscribeEmailDomainHealthCache,
} from '@/lib/emailDomainHealthCache';

export function useEmailDomainHealth(email: string | null) {
  const [recheckGeneration, setRecheckGeneration] = useState(0);
  const [, bump] = useState(0);

  useEffect(() => {
    return subscribeEmailDomainHealthCache(() => bump((n) => n + 1));
  }, []);

  useEffect(() => {
    requestEmailDomainHealth(email, {
      priority: true,
      force: recheckGeneration > 0,
    });
  }, [email, recheckGeneration]);

  const recheck = useCallback(() => {
    setRecheckGeneration((n) => n + 1);
  }, []);

  const { domain, result, checking } = getEmailDomainHealthCacheState(email);

  const health =
    result ??
    (checking
      ? {
          domain,
          checks: PENDING_CHECKS,
          hasFailure: false,
          hasWarning: false,
        }
      : email
      ? {
          domain,
          checks: PENDING_CHECKS,
          hasFailure: false,
          hasWarning: false,
        }
      : {
          domain: null,
          checks: [
            {
              id: 'email',
              label: 'Email',
              status: 'fail' as const,
              detail: 'No email on contact',
            },
          ],
          hasFailure: true,
          hasWarning: false,
        });

  const isChecking =
    checking ||
    (Boolean(email) &&
      health.checks.some((check) => check.status === 'pending'));

  return {
    health,
    checking: isChecking,
    recheck,
  };
}
